import React, { useState } from 'react';

import { styled } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import Paper from '@mui/material/Paper';
import MyMenu from './Menu.tsx';
import { BrowserRouter as Router,Routes,Route, Navigate, useParams } from 'react-router-dom';
import Calendrier from './Calendrier/EventsWithEdit.tsx';
import { Allures } from './Allures/Allures.tsx';
import Main from './Main/Main.tsx';
import AccueilClub from './Main/AccueilClub.tsx';
import Construction from './Construction/Construction.tsx';
import MesSeances from './Construction/MesSeances.tsx';
import Modeles from './Modeles/Modeles.tsx';
import TempsPassage from './Passages/TempsPassage.tsx';
import CalendrierSeances from './Planning/CalendrierSeances.tsx';
import JournalEntrainement from './Journal/JournalEntrainement.tsx';
import Bracelet from './Passages/Bracelet.tsx';
import HeureLever from './Matinee/HeureLever.tsx';
import Preferences from './Preferences/Preferences.tsx';
import Athletes from './Athletes/Athletes.tsx';
import Utilisateurs from './Utilisateurs/Utilisateurs.tsx';
import ClubsAdmin, { CHEMIN_CLUBS_ADMIN } from './Administration/ClubsAdmin.tsx';
import { accesPageClub, estAdmin, estAdministrateurSeul, estCoach, jetonInvitationDeLAdresse, useMoi } from './Auth/moi.ts';
import { FournisseurEmplacementBarre } from './BarrePage.tsx';
import ChoixProfil from './Auth/ChoixProfil.tsx';
import RejoindreCoach from './Auth/RejoindreCoach.tsx';
import InvitationsCoach from './Auth/InvitationsCoach.tsx';
import InvitationsClub from './Auth/InvitationsClub.tsx';
import Club from './Club/Club.tsx';
import Confidentialite, { CHEMIN_CONFIDENTIALITE } from './Confidentialite/Confidentialite.tsx';
import GuideNotation, { CHEMIN_GUIDE_NOTATION } from './Guide/GuideNotation.tsx';

// Pages reservees au coach (seances, generateur) : l'athlete est renvoye a
// l'accueil ; rien n'est affiche tant que le role n'est pas connu.
const ReserveCoach = ({ children }: { children: React.ReactElement }) => {
  const moi = useMoi();
  if (!moi) return null;
  return estCoach(moi) ? children : <Navigate to="/" replace />;
};

// Pages de l'entrainement personnel (calendrier, journal) : un
// administrateur de club qui n'entraine pas est renvoye a la page Club.
const SansEntrainement = ({ children }: { children: React.ReactElement }) => {
  const moi = useMoi();
  return estAdministrateurSeul(moi) ? <Navigate to="/club" replace /> : children;
};

// Accueil : synthese du club pour un administrateur qui n'entraine pas.
const Accueil = () => {
  const moi = useMoi();
  return estAdministrateurSeul(moi) ? <AccueilClub /> : <Main />;
};

// Page Club : l'athlete est renvoye a l'accueil (son club et son coach y sont).
const ReserveClub = ({ children }: { children: React.ReactElement }) => {
  const moi = useMoi();
  if (!moi) return null;
  return accesPageClub(moi) ? children : <Navigate to="/" replace />;
};

// Pages reservees a l'administrateur (comptes de l'application).
const ReserveAdmin = ({ children }: { children: React.ReactElement }) => {
  const moi = useMoi();
  if (!moi) return null;
  return estAdmin(moi) ? children : <Navigate to="/" replace />;
};

const Item = styled(Paper)(({ theme }) => ({
  backgroundColor: theme.palette.mode === 'dark' ? '#1A2027' : '#fff',
  ...theme.typography.body2,
  padding: theme.spacing(1),
  textAlign: 'center',
  color: theme.palette.text.secondary,
}));

const AssetViewer = () => {
  const { fileName } = useParams();
  if (!fileName) {
    return <div>Aucune image sélectionnée.</div>;
  }

  return <img src={`/assets/${fileName}`} alt={fileName} style={{ maxWidth: '100%', height: 'auto' }} />;
};

const App = ({ user, handleLogout }: { user: any; handleLogout: () => void }) => {
  // Emplacement de la barre de menu ou les pages placent leurs controles.
  const [emplacementBarre, setEmplacementBarre] = useState<HTMLElement | null>(null);
  const moi = useMoi();
  // Lien d'invitation d'un coach (/rejoindre/<jeton>) : traite avant tout,
  // y compris avant le choix du profil (la personne s'inscrit comme athlete).
  const [jetonInvitation, setJetonInvitation] = useState<string | null>(jetonInvitationDeLAdresse);

  if (jetonInvitation) {
    return (
      <>
        <CssBaseline />
        <RejoindreCoach
          jeton={jetonInvitation}
          moi={moi}
          handleLogout={handleLogout}
          terminer={() => {
            window.history.replaceState(null, '', '/');
            setJetonInvitation(null);
          }}
        />
      </>
    );
  }

  // Premiere connexion : le profil (athlete ou coach) est choisi avant tout.
  if (moi && !moi.role) {
    return (
      <>
        <CssBaseline />
        <ChoixProfil moi={moi} handleLogout={handleLogout} />
      </>
    );
  }

  return (
    <>
      <React.StrictMode>
        <CssBaseline />
        <Router>
        <FournisseurEmplacementBarre value={emplacementBarre}>
        <MyMenu user={user} handleLogout={handleLogout} props={undefined} refEmplacement={setEmplacementBarre}/>
        {moi && moi.invitations?.length > 0 && <InvitationsCoach invitations={moi.invitations} />}
        {moi && (moi.invitationsClub?.length ?? 0) > 0 && <InvitationsClub invitations={moi.invitationsClub ?? []} />}
        <Routes>
            <Route path="/" element={<Accueil />} />
            <Route path="/allures" element={<Allures handleLogout={handleLogout} />} />
            <Route path="/calendrier" element={<Calendrier />} />
            <Route path="/assets/:fileName" element={<AssetViewer />} />
            <Route path="/seances" element={<ReserveCoach><MesSeances /></ReserveCoach>} />
            <Route path="/seances/nouvelle" element={<ReserveCoach><Construction /></ReserveCoach>} />
            <Route path="/seances/:id" element={<ReserveCoach><Construction /></ReserveCoach>} />
            {/* Anciennes adresses de la page (ex-"Construction") */}
            <Route path="/construction" element={<Navigate to="/seances" replace />} />
            <Route path="/construction/*" element={<Navigate to="/seances" replace />} />
            <Route path="/planning" element={<SansEntrainement><CalendrierSeances /></SansEntrainement>} />
            <Route path="/journal" element={<SansEntrainement><JournalEntrainement /></SansEntrainement>} />
            <Route path="/athletes" element={<ReserveCoach><Athletes /></ReserveCoach>} />
            <Route path="/athletes/:id" element={<ReserveCoach><Athletes /></ReserveCoach>} />
            <Route path="/modeles" element={<ReserveCoach><Modeles /></ReserveCoach>} />
            <Route path={CHEMIN_GUIDE_NOTATION} element={<ReserveCoach><GuideNotation /></ReserveCoach>} />
            <Route path="/passages" element={<TempsPassage />} />
            <Route path="/bracelet" element={<Bracelet />} />
            <Route path="/lever" element={<HeureLever />} />
            <Route path="/club" element={<ReserveClub><Club /></ReserveClub>} />
            <Route path="/utilisateurs" element={<ReserveAdmin><Utilisateurs /></ReserveAdmin>} />
            <Route path={CHEMIN_CLUBS_ADMIN} element={<ReserveAdmin><ClubsAdmin /></ReserveAdmin>} />
            <Route path={`${CHEMIN_CLUBS_ADMIN}/:id`} element={<ReserveAdmin><ClubsAdmin /></ReserveAdmin>} />
            <Route path="/preferences" element={<Preferences user={user} />} />
            <Route path={CHEMIN_CONFIDENTIALITE} element={<Confidentialite />} />
          </Routes>
        </FournisseurEmplacementBarre>
        </Router>
      </React.StrictMode>      
    </>
  );
};

export default App;

